<template>
  <div>
    <div class="d-flex justify-space-between">
      <h6 class="h6 pa-0 mt-0 mb-0">
        {{ $t('organization_settings.webhooks_logs_title') }}
      </h6>

      <div v-if="lastUpdateDate" class="pa-0 grey--text text--darken-2">
        <Button
          fab
          icon="mdi-sync"
          size="x-small"
          :loading="isLoading"
          @click="refreshList"
        />
        <span>
          {{
            $t('organization_settings.webhooks_logs_last_update', {
              date: formatDate(lastUpdateDate, 'YYYY-MM-DD HH:mm:ss'),
            })
          }}
        </span>
      </div>
    </div>

    <v-data-table
      data-private
      disable-sort
      :footer-props="footerOptions"
      :headers="headers"
      :items="logs"
      :items-per-page="itemsPerPage"
      :loading="isLoading"
      :options.sync="options"
      :page="currentPage"
      :server-items-length="totalItems"
    >
      <template v-slot:item.responseStatus="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :color="getStatusIconColor(item.responseStatus)"
              v-on="on"
              v-bind="attrs"
              >{{ getStatusIcon(item.responseStatus) }}</v-icon
            >
          </template>

          <span>{{ getStatusTooltipText(item.responseStatus) }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.eventType="{ item }">
        {{ getEventTypeText(item.eventType) }}
      </template>
      <template v-slot:item.createdAt="{ item }">
        {{ formatDate(item.createdAt, 'YYYY-MM-DD HH:mm:ss') }}
      </template>
      <template v-slot:item.details="{ item }">
        <Button icon="mdi-eye" color="blueGrey" @click="showLogDetails(item)" />
      </template>
    </v-data-table>

    <LogDetailModal
      :show="isDetailDialogVisible"
      :logData="selectedLog"
      @close="closeLogDetails"
    />
  </div>
</template>

<script>
import { utils } from 'frontend-js-shared'
import { mapActions } from 'vuex'

import mixin from '@/mixins/mixin.vue'

import LogDetailModal from './LogDetailModal.vue'
import Button from '@/components/UI/Button'

const INITIAL_ITEMS_PER_PAGE = 20

export default {
  name: 'LogList',

  mixins: [mixin],

  components: {
    LogDetailModal,
    Button,
  },

  props: {
    webhookId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      lastUpdateDate: null,
      headers: [
        {
          text: '',
          align: 'center',
          value: 'responseStatus',
          width: 50,
        },
        {
          text: '',
          align: 'start',
          value: 'eventType',
        },
        {
          text: '',
          align: 'start',
          value: 'createdAt',
        },
        {
          text: '',
          align: 'center',
          value: 'details',
          width: 50,
        },
      ],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      logs: [],
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: INITIAL_ITEMS_PER_PAGE,
      isLoading: false,
      isDetailDialogVisible: false,
      selectedLog: null,
    }
  },

  computed: {
    options: {
      get() {
        return {
          page: this.currentPage,
          itemsLength: this.totalItems,
          itemsPerPage: this.itemsPerPage,
        }
      },

      set(event) {
        this.itemsPerPage = event.itemsPerPage
        this.currentPage = event.page

        this.getLogsPage({
          skip: (event.page - 1) * event.itemsPerPage,
          limit: event.itemsPerPage,
        })
      },
    },
  },

  methods: {
    ...mapActions({
      getLogList: 'getWebhookLogs',
    }),

    formatDate(date, format) {
      return utils.dates.formatDate(date, format)
    },

    async getLogsPage(options) {
      try {
        this.isLoading = true

        const response = await this.getLogList({
          ...options,
          webhookId: this.webhookId,
          organizationId: this.organizationId,
        })

        this.totalItems = response?.data?.totalRecordsInData ?? 0
        this.logs = response?.data?.results ?? []
        this.lastUpdateDate = new Date()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    isSuccessfulStatusCode(statusCode = 0) {
      const code = Number(statusCode)
      return code >= 200 && code < 400
    },

    getStatusIcon(statusCode) {
      return this.isSuccessfulStatusCode(statusCode)
        ? 'mdi-check'
        : 'mdi-alert-outline'
    },

    getStatusIconColor(statusCode) {
      return this.isSuccessfulStatusCode(statusCode) ? 'green' : 'red'
    },

    getEventTypeText(eventType) {
      switch (eventType) {
        case 'revCreated':
          return this.$t('organization_settings.webhooks_rev_created_event')
        case 'revUpdated':
          return this.$t('organization_settings.webhooks_rev_updated_event')
        case 'revDeleted':
          return this.$t('organization_settings.webhooks_rev_deleted_event')
        case 'challengeCreated':
          return this.$t(
            'organization_settings.webhooks_challenge_created_event'
          )
        case 'challengeUpdated':
          return this.$t(
            'organization_settings.webhooks_challenge_updated_event'
          )
        case 'challengeDeleted':
          return this.$t(
            'organization_settings.webhooks_challenge_deleted_event'
          )
        case 'challengeRevLinked':
          return this.$t(
            'organization_settings.webhooks_challenge_rev_linked_event'
          )
        case 'accountCreated':
          return this.$t('organization_settings.webhooks_account_created_event')
        case 'accountUpdated':
          return this.$t('organization_settings.webhooks_account_updated_event')
        case 'accountDeleted':
          return this.$t('organization_settings.webhooks_account_deleted_event')
        case 'routineCreated':
          return this.$t('organization_settings.webhooks_routine_created_event')
        case 'routineUpdated':
          return this.$t('organization_settings.webhooks_routine_updated_event')
        case 'routineDeleted':
          return this.$t('organization_settings.webhooks_routine_deleted_event')
        case 'findingCreated':
          return this.$t('organization_settings.webhooks_finding_created_event')
        case 'findingUpdated':
          return this.$t('organization_settings.webhooks_finding_updated_event')
        case 'findingDeleted':
          return this.$t('organization_settings.webhooks_finding_deleted_event')
        case 'organizationUpdated':
          return this.$t(
            'organization_settings.webhooks_organization_updated_event'
          )
        case 'taskCreated':
          return this.$t('organization_settings.webhooks_task_created_event')
        case 'taskUpdated':
          return this.$t('organization_settings.webhooks_task_updated_event')
        case 'taskDeleted':
          return this.$t('organization_settings.webhooks_task_deleted_event')
        default:
          return ''
      }
    },

    getStatusTooltipText(statusCode) {
      return this.isSuccessfulStatusCode(statusCode)
        ? this.$t('organization_settings.webhooks_successful_delivery')
        : this.$t('organization_settings.webhooks_error_delivery', {
            statusCode,
          })
    },

    showLogDetails(log) {
      this.selectedLog = log
      this.isDetailDialogVisible = true
    },

    closeLogDetails() {
      this.isDetailDialogVisible = false
      this.selectedLog = null
    },

    refreshList() {
      this.currentPage = 1

      this.getLogsPage({
        skip: 0,
        limit: this.itemsPerPage,
      })
    },
  },
}
</script>
